import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import Marquee from "react-fast-marquee";
import { ArrowRight, Zap, Recycle, Scissors } from "lucide-react";
import { useSiteSettings } from "@/context/SiteSettingsContext";

export default function Home() {
  const [products, setProducts] = useState([]);
  const settings = useSiteSettings();
  const categories = (settings.categories || []).filter((c) => c.slug !== "tops").slice(0, 5);
  const heroImg = settings.hero_image || "https://images.unsplash.com/photo-1576775068668-c147f14c36f7?w=800";

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data.slice(0, 8))).catch((err) => console.warn("featured load failed", err));
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative border-b-4 border-black overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1494026222377-872a605b54ba?w=1600)', backgroundSize: 'cover', backgroundPosition: 'center', mixBlendMode: 'multiply' }} />
        <div className="relative max-w-[1400px] mx-auto px-6 py-16 md:py-28 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <span className="sticker rotate-[-3deg]" data-testid="hero-badge">NEW DROP // FEB 2026</span>
            <h1 className="font-display text-6xl sm:text-7xl md:text-8xl lg:text-9xl leading-[0.85] mt-6" data-testid="hero-title">
              WEAR<br/>THE<br/><span className="text-[#FF007F]">OUTLAW.</span>
            </h1>
            <p className="mt-6 max-w-md font-mono-b text-base text-black/80" data-testid="hero-sub">
              Custom-made streetwear + curated thrift. Hand-painted jackets, screen-printed tees, and 1 of 1 pieces you won't see on anyone else.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/shop" data-testid="hero-shop-cta" className="inline-flex items-center gap-2 bg-black text-white px-6 py-3 border-2 border-black brutal-shadow hover-nudge font-mono-b font-bold uppercase">
                Shop the drop <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/sell" data-testid="hero-sell-cta" className="inline-flex items-center gap-2 bg-[#D4FF00] px-6 py-3 border-2 border-black brutal-shadow hover-nudge font-mono-b font-bold uppercase">
                Sell your fits
              </Link>
            </div>
          </div>
          <div className="relative">
            {settings.hero_image && (
              <img src={heroImg} alt="OUTLAW-D hero" data-testid="hero-image" className="w-full aspect-[4/5] object-cover border-2 border-black brutal-shadow-lg" />
            )}
            <span className="sticker pink absolute -bottom-4 -left-4 rotate-[-8deg] text-lg px-4 py-2">1 OF 1</span>
            <span className="sticker orange absolute top-4 -right-4 rotate-[6deg]">HAND-PAINTED</span>
          </div>
        </div>
      </section>

      {/* MARQUEE DIVIDER */}
      <div className="bg-[#D4FF00] border-y-2 border-black py-3">
        <Marquee gradient={false} speed={40}>
          {["JACKETS", "TEES", "JEANS", "TOTES", "THRIFTED", "CUSTOM DROPS"].map((t) => (
            <span key={t} className="font-display text-3xl mx-8">★ {t}</span>
          ))}
        </Marquee>
      </div>

      {/* CATEGORIES */}
      <section className="max-w-[1400px] mx-auto px-6 py-16" data-testid="categories-section">
        <h2 className="font-display text-5xl md:text-6xl mb-8">SHOP BY <span className="text-[#FF007F]">CATEGORY</span></h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {categories.map((c) => (
            <Link key={c.slug} to={`/shop/${c.slug}`} data-testid={`cat-${c.slug}`} className="relative aspect-square border-2 border-black brutal-shadow hover-nudge overflow-hidden group">
              <img src={settings.category_images?.[c.slug]} alt={c.label} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-black/30 group-hover:bg-black/10 transition-colors flex items-end p-4">
                <span className="font-display text-white text-3xl">{c.label}</span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-[1400px] mx-auto px-6 py-8" data-testid="featured-section">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <span className="sticker">FRESH ON THE RACK</span>
            <h2 className="font-display text-5xl md:text-6xl mt-3">LATEST DROPS</h2>
          </div>
          <Link to="/shop" className="font-mono-b font-bold uppercase underline underline-offset-4">See all →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
          {products.map((p) => <ProductCard key={p.product_id} product={p} />)}
        </div>
      </section>

      {/* CUSTOM SECTION */}
      <section className="max-w-[1400px] mx-auto px-6 py-16" data-testid="custom-section">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div className="relative order-2 md:order-1">
            <img
              src={settings.custom_image || "https://images.unsplash.com/photo-1600185365483-26d7a4cc7519?w=1200"}
              alt={settings.custom_title || "Custom pieces"}
              className="w-full aspect-[4/5] object-cover border-2 border-black brutal-shadow-lg"
              data-testid="custom-image"
            />
            <span className="sticker orange absolute -top-4 -right-4 rotate-[8deg]">HAND-MADE</span>
            <span className="sticker pink absolute -bottom-4 -left-4 rotate-[-6deg] text-lg px-4 py-2">1 OF 1</span>
          </div>
          <div className="order-1 md:order-2">
            <span className="sticker rotate-[-2deg]">MADE-TO-ORDER</span>
            <h2 className="font-display text-5xl md:text-7xl leading-[0.9] mt-4" data-testid="custom-title">
              {settings.custom_title || "CUSTOM PIECES"}
            </h2>
            <p className="mt-5 font-mono-b max-w-md text-black/80" data-testid="custom-subtitle">
              {settings.custom_subtitle || "One-of-one hand-painted jackets, screen-printed tees, tailored to you. Made in-house."}
            </p>
            <div className="mt-6 flex flex-wrap gap-4">
              <Link to="/shop" data-testid="custom-shop-cta" className="inline-flex items-center gap-2 bg-black text-white px-6 py-3 border-2 border-black brutal-shadow hover-nudge font-mono-b font-bold uppercase">
                Explore custom <ArrowRight className="w-4 h-4" />
              </Link>
              <a href={`https://wa.me/${(settings.contact_whatsapp || "919152763208").replace(/[^0-9]/g, "")}?text=Yo%20OUTLAW-D%20-%20I%20want%20a%20custom%20piece`} target="_blank" rel="noreferrer" data-testid="custom-request-cta" className="inline-flex items-center gap-2 bg-[#D4FF00] px-6 py-3 border-2 border-black brutal-shadow hover-nudge font-mono-b font-bold uppercase">
                Request a piece
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* THRIFT CALLOUT */}
      <section className="mt-20 border-y-4 border-black bg-[#FF007F] text-white" data-testid="thrift-callout">
        <div className="max-w-[1400px] mx-auto px-6 py-16 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <span className="sticker rotate-[-3deg]">SELL WITH US</span>
            <h2 className="font-display text-5xl md:text-7xl leading-[0.9] mt-4">GOT FIRE FITS?<br/>SEND 'EM.</h2>
            <p className="mt-4 font-mono-b max-w-md">List your thrifted clothes on Outlaw-D. We review, approve & drop 'em live. You get paid. Simple.</p>
            <Link to="/sell" data-testid="thrift-cta" className="inline-flex mt-6 items-center gap-2 bg-[#D4FF00] text-black px-6 py-3 border-2 border-black brutal-shadow hover-nudge font-mono-b font-bold uppercase">
              Submit your thrift <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {[
              { icon: <Scissors />, t: "1. UPLOAD" },
              { icon: <Zap />, t: "2. WE APPROVE" },
              { icon: <Recycle />, t: "3. YOU EARN" },
            ].map((s) => (
              <div key={s.t} className="bg-black border-2 border-white p-4 text-center">
                <div className="text-[#D4FF00] flex justify-center">{s.icon}</div>
                <p className="font-display text-2xl mt-3">{s.t}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
